import React, { useCallback } from 'react';
import makeRequest from '../../../lib/fetchService';
import { useTranslation } from 'react-i18next';
import FeedbackMessage from '../FeedbackMessage';
import ErrorMessage from '../ErrorMessage';
import useTracking from './useTracking';
import renderWithKatex from '../../../lib/sofatutorMarkup/renderWithKatex';
import 'katex/dist/katex.min.css';

const useChatHelpers = ({
  threadRef,
  openAiWrapper,
  usageCounter,
  setUsageCounter,
  disableChat,
  triggerSurvey,
  sofabuddyCookieExists
}) => {
  const { t } = useTranslation();
  const { captureException } = useTracking();

  const cleanChatHistory = useCallback((profileId, types) => {
    const history = localStorage.getItem(profileId);
    if (history) {
      const data = JSON.parse(history);
      if (Array.isArray(data)) {
        const filteredData = data.filter(
          entry => !types.includes(entry.sender)
        );
        localStorage.setItem(profileId, JSON.stringify(filteredData));
      }
    }
  }, []);

  const formatTextWithLatex = text => {
    return text
      .split(/(\$\$.*?\$\$|\$.*?\$|\\\(.*?\\\))/g)
      .map(part => {
        if (part.match(/^\$\$.*\$\$|^\$.*\$|^\\\(.*\\\)$/)) {
          const cleanedPart = part.replace(/^\$\$|^\$|\\\(|\$\$|\\\)$/g, '');
          return renderWithKatex(cleanedPart);
        }

        return part;
      })
      .join('');
  };

  const setCookie = useCallback(async threadId => {
    const apiUrl = '/sofabuddy_chat_bot_used';
    await makeRequest(apiUrl, 'PATCH', { thread_id: threadId });
  }, []);

  const retrieveOrCreateThread = useCallback(
    async params => {
      try {
        if (window.Sofatutor.env === 'test') {
          return { id: 'test_thread_id' };
        }

        if (threadRef.current) {
          return await openAiWrapper.executeWithRetry(
            async client =>
              await client.beta.threads.retrieve(threadRef.current.id)
          );
        } else {
          const newThread = await openAiWrapper.executeWithRetry(
            async client => await client.beta.threads.create()
          );
          threadRef.current = newThread;
          await setCookie(newThread.id);
          if (
            window.Sofatutor.profileLevel ||
            window.Sofatutor.user.profile_type === 'parent'
          ) {
            await setClassLevel(newThread.id);
          }
          return newThread;
        }
      } catch (error) {
        captureException(error);
        await params.injectMessage(
          <ErrorMessage message={t('sofabuddy_chat_bot.error_message')} />,
          'error'
        );
      }
    },
    [openAiWrapper, setCookie, captureException, t]
  );

  const setClassLevel = useCallback(
    async threadId => {
      try {
        await openAiWrapper.executeWithRetry(async client => {
          const profileLevel =
            window.Sofatutor.user.profile_type === 'parent'
              ? 6
              : window.Sofatutor.user.level;
          await client.beta.threads.messages.create(threadId, {
            role: 'assistant',
            content: `The Student you are teaching is in class level ${profileLevel}. Use this information to adjust your teaching style.`
          });

          const run = client.beta.threads.runs.stream(threadId, {
            assistant_id: window.Sofatutor.Sofabuddy.openAi.assistantId
          });

          await run.finalRun();
        });
      } catch (error) {
        captureException(error);
      }
    },
    [openAiWrapper, captureException]
  );

  const sendMessage = useCallback(
    async (params, thread) => {
      setUsageCounter(prevCounter => prevCounter - 1);
      if (!sofabuddyCookieExists) triggerSurvey();

      if (window.Sofatutor.env === 'test') {
        await params.streamMessage('This is a test message');
        return;
      }
      let text = '';
      let flaggedCategories = null;

      try {
        await openAiWrapper.executeWithRetry(async client => {
          await client.beta.threads.messages.create(thread.id, {
            role: 'user',
            user: 'sofabuddy',
            content: params.userInput
          });

          try {
            const moderation = await client.moderations.create({
              input: params.userInput,
              model: 'omni-moderation-latest'
            });

            if (moderation.results[0].flagged) {
              flaggedCategories = Object.entries(
                moderation.results[0].categories
              )
                // eslint-disable-next-line no-unused-vars
                .filter(([_, value]) => value === true)
                .map(([key]) => key);
            }
          } catch (moderationError) {
            console.warn('Moderation endpoint failed:', moderationError);
            flaggedCategories = null;
          }

          const run = client.beta.threads.runs.stream(thread.id, {
            assistant_id: window.Sofatutor.Sofabuddy.openAi.assistantId
          });

          run.on('textDelta', async delta => {
            text += delta.value || '';
            let formattedMessage = formatTextWithLatex(text);
            await params.streamMessage(formattedMessage);
          });

          await run.finalRun();

          const chatAnswerId = crypto.randomUUID();
          await trackConversation(
            params.userInput,
            text,
            thread.id,
            chatAnswerId,
            flaggedCategories
          );
          await params.injectMessage(
            <FeedbackMessage
              message={t('sofabuddy_chat_bot.feedback_message')}
              threadId={thread.id}
              chatAnswerId={chatAnswerId}
              question={params.userInput}
              answer={text}
            />,
            'feedback'
          );
          if (usageCounter - 1 <= 0) {
            disableChat();
          }
        });
      } catch (error) {
        captureException(error);
        await params.injectMessage(
          <ErrorMessage message={t('sofabuddy_chat_bot.error_message')} />,
          'error'
        );
      }
    },
    [
      openAiWrapper,
      setUsageCounter,
      triggerSurvey,
      sofabuddyCookieExists,
      disableChat,
      captureException,
      t
    ]
  );

  const trackConversation = useCallback(
    async (question, answer, threadId, chatAnswerId, flaggedCategories) => {
      try {
        const trackUrl = window.Sofatutor.Sofabuddy.trackUsageDataUrl;
        await makeRequest(trackUrl, 'POST', {
          question: question,
          answer: answer,
          chat_answer_id: chatAnswerId,
          thread_id: threadId,
          flagged: flaggedCategories
        });
      } catch (error) {
        captureException(error);
      }
    },
    [captureException]
  );

  return {
    cleanChatHistory,
    setCookie,
    retrieveOrCreateThread,
    setClassLevel,
    sendMessage,
    trackConversation
  };
};

export default useChatHelpers;
